import React from 'react';
import { Icon } from '../../design/Icon';
import { useExport } from '../../hooks/useExport';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { useAuth } from '../../contexts/AuthContext';
import { EXPORT_PERMISSION } from '../../layout/navConfig';
import { MenuSeparator, MenuTitle, Popover, usePopover, useToast } from './Overlay';
import { exportFormats } from './exportLogic';

/**
 * Page-header Export menu (mockup VL.exportMenu). extraFormats are listed first, for example
 * [{ format: 'pdf', label: 'PDF summary', description: 'Board-ready one-page summary (.pdf)' }].
 * anchor="card" renders the small ghost button the mockup uses on cards.
 */
export function ExportMenu({ name, endpoint, params, columns, rows, extraFormats = [], anchor = 'page' }) {
  const pop = usePopover();
  const toast = useToast();
  const { label, range } = useHierarchyFilter();
  const { exportAs, busy } = useExport({
    name,
    endpoint,
    params,
    columns,
    rows,
    onDone: (result) => (result.success ? toast(`Exported ${result.filename}`) : toast(result.error, 'alert')),
  });

  const { hasPermission } = useAuth();
  const formats = exportFormats({ endpoint, columns, rows, extraFormats });
  if (formats.length === 0 || !hasPermission(EXPORT_PERMISSION)) return null;

  const button =
    anchor === 'card' ? (
      <button type="button" className="btn btn-ghost btn-sm" aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle} disabled={busy}>
        <Icon name="download" className="ico-sm" />
        Export
      </button>
    ) : (
      <button type="button" className="btn" aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle} disabled={busy}>
        <Icon name="download" />
        Export
        <Icon name="chevD" className="ico-sm" />
      </button>
    );

  return (
    <Popover open={pop.open} onClose={pop.close} align="right" width={300} label={`Export ${name}`} anchor={button}>
      <MenuTitle>Export {name}</MenuTitle>
      {formats.map((f) => (
        <button
          key={f.format}
          type="button"
          className="menu-item"
          data-format={f.format}
          onClick={() => {
            pop.close();
            exportAs(f.format);
          }}
        >
          <Icon name={f.format === 'pdf' ? 'file' : 'download'} />
          <span>
            <span style={{ display: 'block' }}>{f.label}</span>
            <span className="caption">{f.description}</span>
          </span>
        </button>
      ))}
      <MenuSeparator />
      <div className="caption" style={{ padding: '4px 9px' }}>
        Uses current filters: {label}, {range.rangeLabel}
      </div>
    </Popover>
  );
}
